import React, { useState, useEffect } from 'react';
import '../../styles/getStarted.css';
import '../../styles/dashboard.css';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { projectService } from '../../services/projectService.js';
import { storage } from '../../utils/localStorage.js';
import { POLLING_INTERVAL } from '../../utils/constants.js';
import ProjectList from './ProjectList.jsx';

const Dashboard = () => {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  // Fetch dashboard data
  const fetchDashboard = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError('');

    try {
     
      
      const response = await projectService.fetchDashboard();
      
      
      const projects = response.projects || [];
     
      setProjects(projects);
      setLastUpdated(new Date());
      
      if (showLoading) setLoading(false);
    } catch (error) {
      console.error('Dashboard: Fetch failed with error:', error);
      console.error('Dashboard: Error type:', typeof error);
      console.error('Dashboard: Error message:', error.message);
      
      // If API fails, try to load from localStorage
      const cachedProjects = storage.getProjects();
      if (cachedProjects && cachedProjects.length > 0) {
        console.log('Dashboard: Loading cached projects:', cachedProjects.length);
        setProjects(cachedProjects);
        setError('Showing cached data. Unable to fetch latest updates.');
      } else {
        console.log('Dashboard: No cached data available');
        const errorMessage = error?.message || error?.error || 'Failed to load dashboard';
        setError(errorMessage);
      }
      
      if (showLoading) setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchDashboard();
  }, []);

  // Polling for updates (only if we have a successful connection)
  useEffect(() => {
    /*if (error && !error.includes('cached')) {
      // Don't poll if we have connection errors
      return;
    }

    const interval = setInterval(() => {
      fetchDashboard(false); // Don't show loading for background updates
    }, POLLING_INTERVAL);

    return () => clearInterval(interval);*/
  }, [error]);

  // Handle project updates
  const handleProjectUpdate = (updatedProject) => {
    setProjects(prevProjects => 
      prevProjects.map(project => 
        project.project_id === updatedProject.project_id 
          ? { ...project, ...updatedProject }
          : project
      )
    );
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-loading">
          <div className="loading-spinner-modern">
            <div className="spinner-ring"></div>
            <div className="spinner-ring"></div>
            <div className="spinner-ring"></div>
          </div>
          <div className="loading-text">Loading your workspace...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Enhanced Welcome Hero Section */}
      <div className="dashboard-hero-enhanced">
        <div className="hero-background-enhanced">
          <div className="hero-gradient-orb"></div>
          <div className="hero-floating-particles">
            <div className="particle"></div>
            <div className="particle"></div>
            <div className="particle"></div>
            <div className="particle"></div>
            <div className="particle"></div>
          </div>
          <div className="hero-grid-pattern"></div>
        </div>
        
        <div className="hero-content-enhanced">
          <div className="hero-text-section">
            <div className="hero-greeting">
              <span className="greeting-time">Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}</span>
              <div className="hero-main-title">
                Welcome back, <span className="hero-name-gradient">{user?.name || 'Developer'}</span>
              </div>
              <p className="hero-description">
                Your development workspace is ready. Continue building the future, one project at a time.
              </p>
            </div>
          </div>
          
          <div className="hero-stats-section">
            <div className="stats-container">
              <div className="stat-card-enhanced running">
                <div className="stat-icon">
                  <i className="fas fa-play-circle"></i>
                </div>
                <div className="stat-content">
                  <div className="stat-number-enhanced">{projects.filter(p => p.container_status === 'running').length}</div>
                  <div className="stat-label-enhanced">Running</div>
                </div>
                <div className="stat-decoration"></div>
              </div>
              
              <div className="stat-card-enhanced active">
                <div className="stat-icon">
                  <i className="fas fa-folder-open"></i>
                </div>
                <div className="stat-content">
                  <div className="stat-number-enhanced">{projects.length}</div>
                  <div className="stat-label-enhanced">Total Projects</div>
                </div>
                <div className="stat-decoration"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="dashboard-alert-enhanced">
          <div className="alert-icon">
            <i className="fas fa-exclamation-triangle"></i>
          </div>
          <div className="alert-content">
            <div className="alert-title">Connection Issue</div>
            <div className="alert-message">{error}</div>
          </div>
          <button onClick={() => fetchDashboard()} className="alert-action">
            <i className="fas fa-retry"></i>
            Retry
          </button>
        </div>
      )}

      {/* Enhanced Projects Section */}
      <div className="projects-section-enhanced">
        <div className="section-header-enhanced">
          <div className="section-title-group">
            <h2 className="section-title-enhanced">Your Projects</h2>
          </div>
          
          <div className="section-actions-enhanced">
            
            <a href="/create-project" className="section-action-btn primary minimal-btn">
              <span className="minimal-btn-icon">
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="20" height="20" rx="6" fill="none" stroke="#Ffffff" strokeWidth="1.5"/>
                  <path d="M10 5v10M5 10h10" stroke="#Ffffff" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </span>
              <span className="minimal-btn-text">New Project</span>

         </a>
          </div>
        </div>

        <ProjectList 
          projects={projects} 
          onProjectUpdate={handleProjectUpdate}
        />

        {projects.length === 0 && !error && (
          <div className="empty-state-simple">
            <div className="empty-background-glow"></div>
            <div className="empty-icon-simple">
              <i className="fas fa-folder-plus"></i>
              <div className="icon-pulse"></div>
            </div>
            <div className="empty-content-simple">
              <h3 className="empty-title-simple">Ready To Launch Your First Project?</h3>
              <p className="empty-description-simple">
                Create your first project to get started with collaborative development.
                <span className="highlight-text"> Build something amazing</span>.
              </p>
              <a href="/create-project" className="empty-action-btn-simple">
                <div className="btn-shine"></div>
                <i className="fas fa-plus"></i>
                <span>Create New Project</span>
              </a>
            </div>
            <div className="floating-dots">
              <div className="dot dot-1"></div>
              <div className="dot dot-2"></div>
              <div className="dot dot-3"></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;